import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Trophy, Target, DollarSign, Calendar } from 'lucide-react';
import { RewardsSkeleton } from './RewardsSkeleton';
import { Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useAuth } from '../../providers/AuthProvider';
import Cookies from 'js-cookie';
import { getAccessTokenOrRedirect } from '../../utils/auth';
import { API_URL, SALES_API_URL } from '../../env';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

interface WeeklyIncentive {
  description: string;
  amount: number;
}

interface TargetAchievements {
  current_quarter: {
    achievement: number;
    target: number;
  };
  last_quarter: {
    achievement: number;
    target: number;
  };
}

interface RewardsPreviewProps {
  isTeamLead?: boolean;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  selectedUser?: number | null;
}

const RewardsPreview: React.FC<RewardsPreviewProps> = ({ isTeamLead, onDateRangeChange, selectedUser }) => {
  const { userInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [isCustomDate, setIsCustomDate] = useState(false);

  interface MarginData {
    margin_percent: number;
    total_profit_amount: number;
  }

  const [targetData, setTargetData] = useState<TargetAchievements | null>(null);
  const [marginData, setMarginData] = useState<MarginData | null>(null);
  const [weeklyIncentives, setWeeklyIncentives] = useState<WeeklyIncentive[]>([]);
  const [totalWeeklyIncentive, setTotalWeeklyIncentive] = useState(0);

  const calculateDateRange = (period: string): [dayjs.Dayjs, dayjs.Dayjs] => {
    const now = dayjs();
    
    switch (period) {
      case 'this-month':
        return [now.startOf('month'), now.endOf('month')];
      case 'this-month-to-date':
        return [now.startOf('month'), now];
      case 'last-month':
        return [
          now.subtract(1, 'month').startOf('month'),
          now.subtract(1, 'month').endOf('month')
        ];
      case 'this-quarter-to-date':
        return [now.startOf('quarter'), now];
      case 'last-quarter':
        return [
          now.subtract(1, 'quarter').startOf('quarter'),
          now.subtract(1, 'quarter').endOf('quarter')
        ];
      case 'this-year-to-date':
        return [now.startOf('year'), now];
      case 'last-year':
        return [
          now.subtract(1, 'year').startOf('year'),
          now.subtract(1, 'year').endOf('year')
        ];
      case 'this-year':
        return [now.startOf('year'), now.endOf('year')];
      default:
        return [now.startOf('month'), now.endOf('month')];
    }
  };

  useEffect(() => {
    if (!isCustomDate) {
      const [start, end] = calculateDateRange(selectedPeriod);
      setDateRange([start, end]);
      onDateRangeChange?.(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    }
  }, [selectedPeriod, isCustomDate, onDateRangeChange]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo?.userId) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);

        // Get access token from cookies
        const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));

        const headers = {
          'Authorization': `Bearer ${jwt}`
        };
        
        // Fetch weekly incentives
        const userId = selectedUser || userInfo.userId;
        const incentivesResponse = await fetch(
          `${SALES_API_URL}/api/config/weekly-incentive/simulate/${userId}`,
          { headers }
        );
        if (!incentivesResponse.ok) {
          throw new Error('Failed to fetch weekly incentives');
        }
        const incentivesData = await incentivesResponse.json();
        setWeeklyIncentives(incentivesData.data?.incentives || []);
        setTotalWeeklyIncentive(incentivesData.data?.total_incentive || 0);

        // Fetch target achievements
        const targetParams = new URLSearchParams();
        if (selectedUser) {
          targetParams.append('user_id', selectedUser.toString());
        }
        const targetResponse = await fetch(
          `${API_URL}/api/target-achievements/${targetParams.toString() ? `?${targetParams.toString()}` : ''}`,
          { headers }
        );
        if (!targetResponse.ok) {
          throw new Error('Failed to fetch target achievements');
        }
        const targetData = await targetResponse.json();
        setTargetData(targetData);

        // Fetch net profit margin
        const marginParams = new URLSearchParams();
        if (selectedUser) {
          marginParams.append('user_id', selectedUser.toString());
        }
        const marginResponse = await fetch(
          `${API_URL}/api/net-profit-margin/${marginParams.toString() ? `?${marginParams.toString()}` : ''}`,
          { headers }
        );
        if (!marginResponse.ok) {
          throw new Error('Failed to fetch net profit margin');
        }
        const data = await marginResponse.json();
        setMarginData(data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userInfo, dateRange, selectedUser]);

  if (isLoading) {
    return <RewardsSkeleton />;
  }

  const calculatePercentage = (achievement: number, target: number) => {
    if (!target) return 0;
    return Math.min(Math.round((achievement / target) * 100), 100);
  };

  const currentQuarterPercentage = targetData 
    ? calculatePercentage(targetData.current_quarter.achievement, targetData.current_quarter.target)
    : 0;

  const previousQuarterPercentage = targetData
    ? calculatePercentage(targetData.last_quarter.achievement, targetData.last_quarter.target)
    : 0;

  const calculateQuarterlyIncentive = () => {
    if (!targetData || !marginData) return 0;

    const currentQuarterPercentage = calculatePercentage(
      targetData.current_quarter.achievement,
      targetData.current_quarter.target
    );
    const lastQuarterPercentage = calculatePercentage(
      targetData.last_quarter.achievement,
      targetData.last_quarter.target
    );

    if (currentQuarterPercentage >= 100 && 
        lastQuarterPercentage >= 30 && 
        (isTeamLead ? marginData.margin_percent >= 15 : marginData.margin_percent >= 15)) {
      return Math.round(marginData.total_profit_amount * 0.01);
    }
    return 0;
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    iconBg: string;
    accent?: string;
  }> = ({ title, value, icon, iconBg, accent = 'text-slate-900' }) => (
    <div className="w-full bg-white border border-slate-200 rounded-lg p-4 sm:p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
        <span className="text-sm font-medium text-slate-600">{title}</span>
      </div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );

  const quarterlyIncentive = calculateQuarterlyIncentive();
  const quarterLabel = `Q${dayjs().quarter()} ${dayjs().year()}`;
  const currency = userInfo?.company_base_currency_symbol || '$';
  const targetAmount = targetData?.current_quarter?.target ?? 0;
  const achievement = targetData?.current_quarter?.achievement ?? 0;
  const shortfall = Math.max(0, targetAmount - achievement);
  return (
    <Card className="rounded shadow p-4">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <CardTitle className="flex items-center gap-2 text-xl font-semibold">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Rewards & Incentives
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Efforts-style concise metric cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title={`${quarterLabel} Target`}
            value={`${currency}${targetAmount.toLocaleString()}`}
            icon={<Target className="h-5 w-5 text-blue-600" />}
            iconBg="bg-blue-100"
          />
          <StatCard
            title={`${quarterLabel} Shortfall`}
            value={`${currency}${shortfall.toLocaleString()}`}
            icon={<Target className="h-5 w-5 text-red-600" />}
            iconBg="bg-red-100"
            accent="text-red-600"
          />
          <StatCard
            title={`Current Quarter Incentive`}
            value={`${currency}${quarterlyIncentive.toLocaleString()}`}
            icon={<DollarSign className="h-5 w-5 text-violet-600" />}
            iconBg="bg-violet-100"
          />
        </div>

        {/* Weekly Incentives Section */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-xl font-semibold mb-6">
              <Calendar className="h-5 w-5 text-slate-900" />
              This Week's Incentives
            </CardTitle>
          </div>

          <div className="bg-purple-50 p-4 sm:p-5 rounded-lg border border-purple-100">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-purple-600">Total Incentive</span>
              </div>
              <span className="text-2xl font-semibold text-slate-900">
                ₹{totalWeeklyIncentive.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg">
            <div className="flex items-center gap-2 p-4 border-b">
              <DollarSign className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-slate-900">Incentive Breakdown</h4>
            </div>
            <div className="divide-y">
              {weeklyIncentives.map((item, index) => (
                <div key={index} className="p-4 hover:bg-slate-50 transition-colors duration-150">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="font-medium text-slate-900">{item.description}</span>
                    <span className="text-sm font-medium text-emerald-600">
                      ₹{item.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RewardsPreview;
